import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2, Edit2, X, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/templates`);
      setTemplates(res.data.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async (templateId) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/templates/${templateId}`, {
        label: editLabel,
        message: editMessage,
      });
      setEditingId(null);
      fetchTemplates();
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = async () => {
    if (!newLabel.trim() || !newMessage.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/templates`, {
        label: newLabel.trim(),
        message: newMessage.trim(),
      });
      setNewLabel('');
      setNewMessage('');
      setShowAdd(false);
      fetchTemplates();
    } catch (error) {
      console.error('Add failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (tpl) => {
    setEditingId(tpl.template_id);
    setEditLabel(tpl.label);
    setEditMessage(tpl.message);
  };

  const inputClass = "w-full border border-slate-700 rounded h-10 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500";
  const labelClass = "text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block";

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <header className="sticky top-0 z-50 bg-[#0E1525] border-b border-slate-800 h-16 flex items-center px-6">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin-secure-bd2026')}
              data-testid="back-to-dashboard"
              className="flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>
          <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Settings</span>
        </div>
      </header>

      <div className="p-6 max-w-3xl mx-auto">
        {/* WhatsApp Templates */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-400" />
              <h2 className="text-lg font-bold text-slate-100">WhatsApp Message Templates</h2>
            </div>
            <button
              onClick={() => setShowAdd(true)}
              data-testid="add-template-button"
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-semibold transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Template
            </button>
          </div>

          <p className="text-xs text-slate-400 mb-4">
            Use <span className="font-mono bg-slate-800 text-slate-200 px-1 rounded">{'{name}'}</span> for candidate name and <span className="font-mono bg-slate-800 text-slate-200 px-1 rounded">{'{id}'}</span> for their unique ID.
          </p>

          {loading ? (
            <div className="text-sm text-slate-400 py-8 text-center">Loading templates...</div>
          ) : (
            <div className="space-y-3">
              {/* Add New Template Form */}
              {showAdd && (
                <div className="bg-[#0E1525] border-2 border-blue-500/40 rounded-lg p-4 space-y-3" data-testid="add-template-form">
                  <div>
                    <label className={labelClass}>Template Name</label>
                    <input
                      type="text"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      placeholder="e.g. Welcome Message"
                      data-testid="new-template-label"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Message</label>
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Hello {name}, ..."
                      rows={3}
                      data-testid="new-template-message"
                      className="w-full border border-slate-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAdd}
                      disabled={saving || !newLabel.trim() || !newMessage.trim()}
                      data-testid="save-new-template"
                      className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white px-4 py-2 rounded text-xs font-semibold transition-colors"
                    >
                      <Save className="h-3.5 w-3.5" />
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => { setShowAdd(false); setNewLabel(''); setNewMessage(''); }}
                      className="text-slate-400 hover:text-slate-200 px-3 py-2 text-xs font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Existing Templates */}
              {templates.map((tpl) => (
                <div key={tpl.template_id} className="bg-[#0E1525] border border-slate-800 rounded-lg p-4" data-testid={`template-${tpl.template_id}`}>
                  {editingId === tpl.template_id ? (
                    <div className="space-y-3">
                      <div>
                        <label className={labelClass}>Template Name</label>
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          data-testid={`edit-label-${tpl.template_id}`}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Message</label>
                        <textarea
                          value={editMessage}
                          onChange={(e) => setEditMessage(e.target.value)}
                          rows={3}
                          data-testid={`edit-message-${tpl.template_id}`}
                          className="w-full border border-slate-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(tpl.template_id)}
                          disabled={saving}
                          data-testid={`save-${tpl.template_id}`}
                          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white px-4 py-2 rounded text-xs font-semibold transition-colors"
                        >
                          <Save className="h-3.5 w-3.5" />
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-slate-400 hover:text-slate-200 px-3 py-2 text-xs font-semibold"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-slate-100">{tpl.label}</h3>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEdit(tpl)}
                            data-testid={`edit-btn-${tpl.template_id}`}
                            className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors rounded hover:bg-blue-900/20"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(tpl.template_id)}
                            data-testid={`delete-btn-${tpl.template_id}`}
                            className="p-1.5 text-slate-400 hover:text-red-400 transition-colors rounded hover:bg-red-900/30"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300 leading-relaxed">{tpl.message}</p>
                    </div>
                  )}
                </div>
              ))}

              {templates.length === 0 && !showAdd && (
                <div className="text-center py-8 text-slate-400 text-sm">No templates yet. Add your first one.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
